/**
 * WebAuthn (Passkey) helpers for Zoobook 3.
 *
 * Communicates with Platform Edge Functions:
 * - webauthn-register: creates credential options, stores public key
 * - webauthn-authenticate: creates assertion options, verifies
 */

// Staging (SUPABASE_FORCE=true) uses Platform staging instance;
// production uses the production Platform instance.
// Must match onboarding.ts selection logic.
const isStaging = process.env.SUPABASE_FORCE === 'true';

const PLATFORM_URL = isStaging
  ? process.env.PLATFORM_SUPABASE_URL_STAGING || ''
  : process.env.PLATFORM_SUPABASE_URL || '';

const PLATFORM_ANON_KEY = isStaging
  ? process.env.PLATFORM_SUPABASE_ANON_KEY_STAGING || ''
  : process.env.PLATFORM_SUPABASE_ANON_KEY || '';

/** Standard headers for Platform Edge Function calls. */
function platformHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${PLATFORM_ANON_KEY}`,
    apikey: PLATFORM_ANON_KEY,
  };
}

/**
 * Check if WebAuthn is supported in the current browser.
 */
export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator.credentials !== 'undefined'
  );
}

/**
 * Check if platform authenticator (Touch ID / Windows Hello / etc.) is available.
 *
 * In Electron, Chromium's WebAuthn layer may report false negatives for
 * platform authenticators even on Macs with Touch ID. We fall back to
 * checking the user agent + platform as a heuristic.
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  try {
    const result = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    if (result) return true;

    // Electron fallback: Chromium in Electron often returns false even on Macs
    // with Touch ID. If we're on macOS with WebAuthn support, assume Touch ID
    // is available and let the registration attempt itself determine success.
    const isElectron = navigator.userAgent.includes('Electron');
    const isMac = navigator.platform === 'MacIntel' || navigator.platform === 'MacARM';
    if (isElectron && isMac) return true;

    return false;
  } catch {
    return false;
  }
}

/**
 * Get a user-friendly label for the passkey button based on the platform.
 * Best-effort detection — browser APIs don't distinguish Face ID vs Touch ID.
 */
export function getPasskeyLabel(): string {
  if (typeof navigator === 'undefined') return 'Sign in with passkey';
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return 'Sign in with Face ID';
  if (/Macintosh/.test(ua)) return 'Sign in with Touch ID';
  if (/Windows/.test(ua)) return 'Sign in with Windows Hello';
  if (/Android/.test(ua)) return 'Sign in with fingerprint';
  return 'Sign in with passkey';
}

// -- Helpers for base64url encoding --

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// ─── Electron Touch ID helpers ───

/** Detect if running in Electron with Touch ID IPC available. */
function isElectronWithTouchID(): boolean {
  const api = (window as unknown as Record<string, unknown>).zoobook as Record<string, unknown> | undefined;
  return typeof api?.promptTouchID === 'function';
}

/** Access Electron IPC API from preload. */
function getElectronApi() {
  return (window as unknown as Record<string, unknown>).zoobook as {
    promptTouchID: (reason: string) => Promise<boolean>;
    canPromptTouchID: () => Promise<boolean>;
  } | undefined;
}

/**
 * Electron-native passkey registration.
 * Uses systemPreferences.promptTouchID() + Web Crypto keypair.
 */
async function startPasskeyRegistrationElectron(
  accessToken: string,
  orgId?: string,
): Promise<{ credentialId: string }> {
  const api = getElectronApi();
  if (!api) throw new Error('Electron API not available');

  // 1. Prompt Touch ID
  const ok = await api.promptTouchID('Register passkey for Zoobook');
  if (!ok) throw new Error('Touch ID cancelled');

  // 2. Generate ECDSA P-256 keypair
  const keyPair = await crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    true,
    ['sign', 'verify'],
  );
  const pubRaw = await crypto.subtle.exportKey('raw', keyPair.publicKey);
  const pubB64 = btoa(String.fromCharCode(...new Uint8Array(pubRaw)));

  // Generate credential ID
  const idBytes = new Uint8Array(32);
  crypto.getRandomValues(idBytes);
  const credentialId = bufferToBase64url(idBytes.buffer as ArrayBuffer);

  // Store private key locally
  const privJwk = await crypto.subtle.exportKey('jwk', keyPair.privateKey);
  localStorage.setItem(`zbk3_passkey_${credentialId}`, JSON.stringify(privJwk));

  // 3. Send public key to server
  const res = await fetch(`${PLATFORM_URL}/functions/v1/webauthn-register`, {
    method: 'POST',
    headers: platformHeaders(),
    body: JSON.stringify({
      action: 'verify',
      org_id: orgId,
      access_token: accessToken,
      credential: {
        id: credentialId,
        response: { attestationObject: pubB64, transports: ['internal'] },
      },
    }),
  });

  if (!res.ok) {
    localStorage.removeItem(`zbk3_passkey_${credentialId}`);
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? 'Failed to store passkey');
  }

  return { credentialId };
}

/**
 * Start passkey registration for the current user.
 * In Electron: native Touch ID + Web Crypto keypair.
 * In browsers: standard WebAuthn API.
 */
export async function startPasskeyRegistration(
  accessToken: string,
  orgId?: string,
): Promise<{
  credentialId: string;
}> {
  if (isElectronWithTouchID()) {
    return startPasskeyRegistrationElectron(accessToken, orgId);
  }
  // 1. Get registration options from server
  const optionsRes = await fetch(`${PLATFORM_URL}/functions/v1/webauthn-register`, {
    method: 'POST',
    headers: platformHeaders(),
    body: JSON.stringify({ action: 'options', org_id: orgId, access_token: accessToken }),
  });

  if (!optionsRes.ok) {
    const body = await optionsRes.json().catch(() => ({}));
    throw new Error(body.error ?? 'Failed to get registration options');
  }

  const options = await optionsRes.json();

  // 2. Create credential via browser API
  const publicKey: PublicKeyCredentialCreationOptions = {
    challenge: base64urlToBuffer(options.challenge),
    rp: {
      name: options.rp.name,
      id: options.rp.id,
    },
    user: {
      id: base64urlToBuffer(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    pubKeyCredParams: options.pubKeyCredParams,
    timeout: options.timeout ?? 60000,
    attestation: options.attestation ?? 'none',
    authenticatorSelection: options.authenticatorSelection ?? {
      residentKey: 'preferred',
      userVerification: 'required',
    },
    excludeCredentials: (options.excludeCredentials ?? []).map(
      (cred: { id: string; type: string }) => ({
        id: base64urlToBuffer(cred.id),
        type: cred.type,
      }),
    ),
  };

  const credential = (await navigator.credentials.create({
    publicKey,
  })) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error('Passkey registration was cancelled');
  }

  const attestationResponse = credential.response as AuthenticatorAttestationResponse;

  // 3. Send attestation to server for verification
  const verifyRes = await fetch(`${PLATFORM_URL}/functions/v1/webauthn-register`, {
    method: 'POST',
    headers: platformHeaders(),
    body: JSON.stringify({
      action: 'verify',
      org_id: orgId,
      access_token: accessToken,
      credential: {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          attestationObject: bufferToBase64url(attestationResponse.attestationObject),
          clientDataJSON: bufferToBase64url(attestationResponse.clientDataJSON),
          transports:
            (
              attestationResponse as unknown as { getTransports?: () => string[] }
            ).getTransports?.() ?? [],
        },
      },
    }),
  });

  if (!verifyRes.ok) {
    const body = await verifyRes.json().catch(() => ({}));
    throw new Error(body.error ?? 'Failed to verify registration');
  }

  return { credentialId: credential.id };
}

// ─── Passkey Discovery (no zoo config needed) ───

/**
 * Discover + authenticate via passkey in one step.
 * Uses discoverable credentials (resident keys) — the browser prompts
 * Face ID / Touch ID / Windows Hello without needing the user's email.
 *
 * Platform resolves the credential → identifies the zoo → returns session + config.
 */
type DiscoverResult = {
  access_token: string;
  refresh_token: string;
  zoo_config: {
    slug: string;
    name: string;
    logoUrl: string | null;
    supabaseUrl: string;
    supabaseAnonKey: string;
    orgId: string;
  };
};

/**
 * Find locally stored Electron passkey credential IDs.
 */
function getStoredPasskeyIds(): string[] {
  const ids: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith('zbk3_passkey_')) {
      ids.push(key.replace('zbk3_passkey_', ''));
    }
  }
  return ids;
}

export async function discoverWithPasskey(): Promise<DiscoverResult> {
  // Electron path: native Touch ID + stored credential
  if (isElectronWithTouchID()) {
    return discoverWithPasskeyElectron();
  }
  // Browser path: standard WebAuthn discoverable credentials
  return discoverWithPasskeyWebAuthn();
}

/**
 * Electron-native passkey login.
 * Uses stored credential ID + Touch ID verification → server generates session.
 * Walks through all locally stored credentials if the first few are stale
 * (server-side deleted), cleaning up as it goes.
 */
async function discoverWithPasskeyElectron(): Promise<DiscoverResult> {
  const api = getElectronApi();
  if (!api) throw new Error('Electron API not available');

  const credIds = getStoredPasskeyIds();
  if (credIds.length === 0) {
    throw new Error('No passkeys registered on this device');
  }

  const ok = await api.promptTouchID('Sign in to Zoobook');
  if (!ok) throw new Error('Touch ID cancelled');

  // Try each stored credential until one succeeds. Remove locally any that
  // the server no longer recognizes — they were deleted or never made it to
  // the DB. This keeps localStorage clean without manual DevTools surgery.
  let lastError = 'Passkey login failed';
  for (const credId of credIds) {
    const res = await fetch(`${PLATFORM_URL}/functions/v1/webauthn-authenticate`, {
      method: 'POST',
      headers: platformHeaders(),
      body: JSON.stringify({ action: 'electron-discover', credential_id: credId }),
    });

    if (res.ok) return await res.json();

    const body = await res.json().catch(() => ({}));
    const errText = (body as { error?: string }).error ?? '';
    if (/not recognized/i.test(errText)) {
      localStorage.removeItem(`zbk3_passkey_${credId}`);
      lastError = errText;
      continue;
    }
    throw new Error(errText || 'Passkey login failed');
  }

  throw new Error(`${lastError}. Register a new passkey first.`);
}

/**
 * Standard WebAuthn passkey discovery (Chrome, Safari).
 */
async function discoverWithPasskeyWebAuthn(): Promise<DiscoverResult> {
  const optionsRes = await fetch(`${PLATFORM_URL}/functions/v1/webauthn-authenticate`, {
    method: 'POST',
    headers: platformHeaders(),
    body: JSON.stringify({ action: 'discover-options' }),
  });

  if (!optionsRes.ok) {
    const body = await optionsRes.json().catch(() => ({}));
    throw new Error(body.error ?? 'Failed to get passkey options');
  }

  const options = await optionsRes.json();

  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge: base64urlToBuffer(options.challenge),
      timeout: options.timeout ?? 60000,
      rpId: options.rpId,
      userVerification: 'required',
      allowCredentials: [],
    },
  })) as PublicKeyCredential | null;

  if (!credential) throw new Error('Passkey authentication was cancelled');

  const ar = credential.response as AuthenticatorAssertionResponse;

  const verifyRes = await fetch(`${PLATFORM_URL}/functions/v1/webauthn-authenticate`, {
    method: 'POST',
    headers: platformHeaders(),
    body: JSON.stringify({
      action: 'discover-verify',
      credential: {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          authenticatorData: bufferToBase64url(ar.authenticatorData),
          clientDataJSON: bufferToBase64url(ar.clientDataJSON),
          signature: bufferToBase64url(ar.signature),
          userHandle: ar.userHandle ? bufferToBase64url(ar.userHandle) : null,
        },
      },
    }),
  });

  if (!verifyRes.ok) {
    const body = await verifyRes.json().catch(() => ({}));
    throw new Error(body.error ?? 'Passkey authentication failed');
  }

  return await verifyRes.json();
}

/**
 * Start Conditional UI passkey flow — passkey appears in the email
 * field's autofill dropdown (WebAuthn Conditional Mediation).
 *
 * Call on mount with an AbortController. If the user selects a passkey
 * from the autofill, returns session + config. If they type an email
 * instead, returns null (abort the request before navigating).
 *
 * Requires: `autocomplete="username webauthn"` on the email input.
 */
export async function startConditionalPasskey(abortController: AbortController): Promise<{
  access_token: string;
  refresh_token: string;
  zoo_config: {
    slug: string;
    name: string;
    logoUrl: string | null;
    supabaseUrl: string;
    supabaseAnonKey: string;
    orgId: string;
  };
} | null> {
  // Check if conditional mediation is supported
  if (
    !isWebAuthnSupported() ||
    !('isConditionalMediationAvailable' in PublicKeyCredential) ||
    !(await (
      PublicKeyCredential as unknown as { isConditionalMediationAvailable: () => Promise<boolean> }
    ).isConditionalMediationAvailable())
  ) {
    return null;
  }

  // Get discover options
  const optionsRes = await fetch(`${PLATFORM_URL}/functions/v1/webauthn-authenticate`, {
    method: 'POST',
    headers: platformHeaders(),
    body: JSON.stringify({ action: 'discover-options' }),
    signal: abortController.signal,
  });

  if (!optionsRes.ok) return null;

  const options = await optionsRes.json();

  // Request credential with conditional mediation (shows in autofill)
  try {
    const credential = (await navigator.credentials.get({
      publicKey: {
        challenge: base64urlToBuffer(options.challenge),
        timeout: 300000, // 5 min — user may interact with autofill later
        rpId: options.rpId,
        userVerification: 'required',
        allowCredentials: [],
      },
      mediation: 'conditional' as CredentialMediationRequirement,
      signal: abortController.signal,
    })) as PublicKeyCredential | null;

    if (!credential) return null;

    const assertionResponse = credential.response as AuthenticatorAssertionResponse;

    const verifyRes = await fetch(`${PLATFORM_URL}/functions/v1/webauthn-authenticate`, {
      method: 'POST',
      headers: platformHeaders(),
      body: JSON.stringify({
        action: 'discover-verify',
        credential: {
          id: credential.id,
          rawId: bufferToBase64url(credential.rawId),
          type: credential.type,
          response: {
            authenticatorData: bufferToBase64url(assertionResponse.authenticatorData),
            clientDataJSON: bufferToBase64url(assertionResponse.clientDataJSON),
            signature: bufferToBase64url(assertionResponse.signature),
            userHandle: assertionResponse.userHandle
              ? bufferToBase64url(assertionResponse.userHandle)
              : null,
          },
        },
      }),
      signal: abortController.signal,
    });

    if (!verifyRes.ok) return null;

    return await verifyRes.json();
  } catch {
    // AbortError or user cancelled — not an error
    return null;
  }
}

// ─── Org-based passkey login (requires zoo config) ───

/**
 * Start passkey login. Returns a Supabase session token pair on success.
 */
export async function startPasskeyLogin(supabaseUrl: string): Promise<{
  access_token: string;
  refresh_token: string;
}> {
  // 1. Get authentication options from server
  const optionsRes = await fetch(`${PLATFORM_URL}/functions/v1/webauthn-authenticate`, {
    method: 'POST',
    headers: platformHeaders(),
    body: JSON.stringify({
      action: 'options',
      supabaseUrl,
    }),
  });

  if (!optionsRes.ok) {
    const body = await optionsRes.json().catch(() => ({}));
    throw new Error(body.error ?? 'Failed to get authentication options');
  }

  const options = await optionsRes.json();

  // 2. Get assertion via browser API
  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge: base64urlToBuffer(options.challenge),
    timeout: options.timeout ?? 60000,
    rpId: options.rpId,
    userVerification: 'required',
    allowCredentials: (options.allowCredentials ?? []).map(
      (cred: { id: string; type: string }) => ({
        id: base64urlToBuffer(cred.id),
        type: cred.type,
      }),
    ),
  };

  const credential = (await navigator.credentials.get({
    publicKey,
  })) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error('Passkey authentication was cancelled');
  }

  const assertionResponse = credential.response as AuthenticatorAssertionResponse;

  // 3. Send assertion to server for verification
  const verifyRes = await fetch(`${PLATFORM_URL}/functions/v1/webauthn-authenticate`, {
    method: 'POST',
    headers: platformHeaders(),
    body: JSON.stringify({
      action: 'verify',
      supabaseUrl,
      credential: {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          authenticatorData: bufferToBase64url(assertionResponse.authenticatorData),
          clientDataJSON: bufferToBase64url(assertionResponse.clientDataJSON),
          signature: bufferToBase64url(assertionResponse.signature),
          userHandle: assertionResponse.userHandle
            ? bufferToBase64url(assertionResponse.userHandle)
            : null,
        },
      },
    }),
  });

  if (!verifyRes.ok) {
    const body = await verifyRes.json().catch(() => ({}));
    throw new Error(body.error ?? 'Passkey authentication failed');
  }

  return await verifyRes.json();
}
